'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FilePlus2,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import {
  createPageFromTemplate,
  PAGE_TEMPLATES,
  type PageTemplate,
} from '@/lib/storefront/section-library';
import type { AgencyBrandProfile } from '@/lib/storefront/agency-brand';
import { pageSlugError, slugifyPageName } from '@/lib/site/page-slugs';
import { collectLinkIssues, type LinkIssue } from '@/lib/site/site-links';

interface PageRow {
  slug: string;
  name: string;
  isPublished: boolean;
  hasUnpublishedChanges: boolean;
  publishedAt: string | null;
  sectionCount: number;
  updatedAt: string | null;
}

interface PageManagerProps {
  agencySlug: string;
  agencyName: string;
  agencyBrand?: AgencyBrandProfile | null;
  currentSlug: string;
  dirty: boolean;
  onClose: () => void;
  onSelectPage: (slug: string) => void;
  onSaveCurrent?: () => Promise<boolean>;
}

type FormMode =
  | { kind: 'create' }
  | { kind: 'rename'; page: PageRow }
  | { kind: 'duplicate'; page: PageRow };

const inputClass =
  'mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink outline-none focus:border-brand';

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function errorMessage(data: any, status: number): string {
  if (data?.errors && typeof data.errors === 'object') {
    const details = Object.values(data.errors).flat().join(' ');
    if (details) return details;
  }
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  return `Request failed (${status})`;
}

export function PageManager({
  agencySlug,
  agencyName,
  agencyBrand,
  currentSlug,
  dirty,
  onClose,
  onSelectPage,
  onSaveCurrent,
}: PageManagerProps) {
  const base = `/api/platform/tenants/${agencySlug}`;
  const [pages, setPages] = useState<PageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<FormMode | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [template, setTemplate] = useState<PageTemplate>('home');
  const [checkingLinks, setCheckingLinks] = useState(false);
  const [linkReport, setLinkReport] = useState<{ page: PageRow; issues: LinkIssue[] }[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${base}/site-pages`, { headers: { Accept: 'application/json' } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, res.status));
      const rows = Array.isArray(data?.pages) ? data.pages : [];
      setPages(rows.map((row: any): PageRow => ({
        slug: String(row.slug ?? ''),
        name: String(row.name ?? ''),
        isPublished: Boolean(row.isPublished),
        hasUnpublishedChanges: Boolean(row.hasUnpublishedChanges),
        publishedAt: typeof row.publishedAt === 'string' ? row.publishedAt : null,
        sectionCount: Number(row.sectionCount ?? 0),
        updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
      })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load pages.');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setForm({ kind: 'create' });
    setName('');
    setSlug('');
    setSlugTouched(false);
    setTemplate('home');
    setError(null);
    setNotice(null);
  };

  const openRename = (page: PageRow) => {
    setForm({ kind: 'rename', page });
    setName(page.name);
    setSlug(page.slug);
    setSlugTouched(true);
    setError(null);
    setNotice(null);
  };

  const openDuplicate = (page: PageRow) => {
    const nextName = `${page.name} copy`;
    setForm({ kind: 'duplicate', page });
    setName(nextName);
    setSlug(slugifyPageName(nextName));
    setSlugTouched(false);
    setError(null);
    setNotice(null);
  };

  const cancelForm = () => {
    if (busy) return;
    setForm(null);
    setError(null);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (form?.kind !== 'rename' && !slugTouched) setSlug(slugifyPageName(value));
  };

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setSlugTouched(true);
  };

  const submitForm = async () => {
    if (!form) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Give the page a name.');
      return;
    }

    const targetSlug = form.kind === 'rename' ? form.page.slug : slug.trim();
    if (form.kind !== 'rename') {
      const slugError = pageSlugError(targetSlug);
      if (slugError) {
        setError(slugError);
        return;
      }
      if (pages.some((page) => page.slug === targetSlug)) {
        setError('A page with this URL already exists.');
        return;
      }
    }

    const willSwitch = form.kind === 'create'
      || form.kind === 'duplicate'
      || (form.kind === 'rename' && form.page.slug === currentSlug);
    if (willSwitch && dirty && !window.confirm('You have unsaved changes on the current page. Continue and lose them?')) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (form.kind === 'create') {
        const starter = createPageFromTemplate(
          template,
          trimmedName,
          agencyBrand ?? { name: agencyName },
        );
        // Header and footer are owned by the home page and shared with every
        // page. Never copy them into a custom page.
        const sections = targetSlug === 'home'
          ? starter.sections
          : starter.sections.filter((section) => section.category !== 'header' && section.category !== 'footer');
        const res = await fetch(`${base}/site-config/${targetSlug}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            name: trimmedName,
            sections,
            settings: starter.settings,
            design_tokens: starter.designTokens,
            version: 1,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(errorMessage(data, res.status));
        onSelectPage(targetSlug);
        return;
      }

      const sourceSlug = form.page.slug;
      const draftRes = await fetch(`${base}/site-config/${sourceSlug}/draft`, {
        headers: { Accept: 'application/json' },
      });
      const draft = await draftRes.json().catch(() => ({}));
      if (!draftRes.ok) throw new Error(errorMessage(draft, draftRes.status));

      const res = await fetch(`${base}/site-config/${targetSlug}`, {
        method: form.kind === 'duplicate' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          sections: Array.isArray(draft.sections) ? draft.sections : [],
          settings: isRecord(draft.settings) ? draft.settings : {},
          design_tokens: isRecord(draft.design_tokens) ? draft.design_tokens : {},
          version: 1,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, res.status));

      if (form.kind === 'duplicate') {
        onSelectPage(targetSlug);
        return;
      }
      if (form.page.slug === currentSlug) {
        onSelectPage(currentSlug);
        return;
      }

      setForm(null);
      await load();
      setNotice(`Renamed “${form.page.name}” to “${trimmedName}”.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The page could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const deletePage = async (page: PageRow) => {
    if (page.slug === 'home') return;
    if (!window.confirm(`Delete “${page.name}”? This cannot be undone.`)) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${base}/site-config/${page.slug}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessage(data, res.status));

      if (form && form.kind !== 'create' && form.page.slug === page.slug) setForm(null);
      if (page.slug === currentSlug) {
        onSelectPage('home');
        return;
      }
      await load();
      setNotice(`Deleted “${page.name}”.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The page could not be deleted.');
    } finally {
      setBusy(false);
    }
  };

  const publishAll = async () => {
    if (!window.confirm('Publish every saved page? This updates the live website.')) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (onSaveCurrent) {
        const saved = await onSaveCurrent();
        if (!saved) {
          setError('The current page could not be saved. Fix the error and try again.');
          return;
        }
      }

      // Re-read the list after saving the current page. A page opened from a
      // direct URL may have just been created by that save and would be missing
      // from the snapshot taken when the dialog opened.
      const listRes = await fetch(`${base}/site-pages`, { headers: { Accept: 'application/json' } });
      const listData = await listRes.json().catch(() => ({}));
      const freshPages: PageRow[] = listRes.ok && Array.isArray(listData?.pages)
        ? listData.pages.map((row: any): PageRow => ({
            slug: String(row.slug ?? ''),
            name: String(row.name ?? ''),
            isPublished: Boolean(row.isPublished),
            hasUnpublishedChanges: Boolean(row.hasUnpublishedChanges),
            publishedAt: typeof row.publishedAt === 'string' ? row.publishedAt : null,
            sectionCount: Number(row.sectionCount ?? 0),
            updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
          }))
        : pages;

      const failures: string[] = [];
      let published = 0;
      for (const page of freshPages) {
        const res = await fetch(`${base}/site-config/${page.slug}/publish`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) published += 1;
        else failures.push(`${page.slug} (${errorMessage(data, res.status)})`);
      }

      await load();
      if (failures.length) {
        setError(`Published ${published} page(s). Failed: ${failures.join('; ')}`);
      } else {
        setNotice(`Published ${published} page(s).`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publishing failed.');
    } finally {
      setBusy(false);
    }
  };

  const selectPage = (page: PageRow) => {
    if (page.slug === currentSlug) {
      onClose();
      return;
    }
    if (dirty && !window.confirm('You have unsaved changes on the current page. Switch pages anyway?')) return;
    onSelectPage(page.slug);
  };

  const checkLinks = async () => {
    setCheckingLinks(true);
    setError(null);
    setNotice(null);
    setLinkReport(null);
    try {
      if (dirty && onSaveCurrent) {
        const saved = await onSaveCurrent();
        if (!saved) {
          setError('The current page could not be saved. Fix the error and try again.');
          return;
        }
      }

      const listRes = await fetch(`${base}/site-pages`, { headers: { Accept: 'application/json' } });
      const listData = await listRes.json().catch(() => ({}));
      if (!listRes.ok) throw new Error(errorMessage(listData, listRes.status));

      const rows: PageRow[] = (Array.isArray(listData?.pages) ? listData.pages : []).map((row: any): PageRow => ({
        slug: String(row.slug ?? ''),
        name: String(row.name ?? ''),
        isPublished: Boolean(row.isPublished),
        hasUnpublishedChanges: Boolean(row.hasUnpublishedChanges),
        publishedAt: typeof row.publishedAt === 'string' ? row.publishedAt : null,
        sectionCount: Number(row.sectionCount ?? 0),
        updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
      }));

      const report: { page: PageRow; issues: LinkIssue[] }[] = [];
      for (const page of rows) {
        const draftRes = await fetch(`${base}/site-config/${page.slug}/draft`, {
          headers: { Accept: 'application/json' },
        });
        const draft = await draftRes.json().catch(() => ({}));
        if (!draftRes.ok) continue;
        const issues = collectLinkIssues(Array.isArray(draft.sections) ? draft.sections : [], rows);
        if (issues.length > 0) report.push({ page, issues });
      }

      setLinkReport(report);
      if (report.length === 0) setNotice('No broken internal links found.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check links.');
    } finally {
      setCheckingLinks(false);
    }
  };

  const formTitle = form?.kind === 'create'
    ? 'New page'
    : form?.kind === 'rename'
      ? `Rename “${form.page.name}”`
      : form?.kind === 'duplicate'
        ? `Duplicate “${form.page.name}”`
        : '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
      <div className="dashboard-root flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-card text-ink shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">Site pages</p>
            <h2 className="text-sm font-semibold text-ink">Manage pages</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg p-1.5 text-muted hover:bg-shell hover:text-ink disabled:opacity-40"
            aria-label="Close pages"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-2">
          <button
            type="button"
            onClick={openCreate}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <FilePlus2 className="h-3.5 w-3.5" />
            New page
          </button>
          <button
            type="button"
            onClick={() => void publishAll()}
            disabled={busy || loading || pages.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-ink hover:bg-shell disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
            Publish all
          </button>
          <button
            type="button"
            onClick={() => void checkLinks()}
            disabled={busy || checkingLinks || loading || pages.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-ink hover:bg-shell disabled:opacity-50"
          >
            {checkingLinks ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            Check links
          </button>
          <p className="ml-auto text-[11px] text-muted">Home supplies the site-wide header and footer.</p>
        </div>

        {error && (
          <div className="flex items-start gap-2 border-b border-rose-100 bg-rose-50 px-5 py-2 text-xs font-semibold text-rose-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => void load()} className="underline decoration-rose-300 underline-offset-2">
              Retry
            </button>
          </div>
        )}
        {notice && (
          <div className="flex items-start gap-2 border-b border-emerald-100 bg-emerald-50 px-5 py-2 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {notice}
          </div>
        )}
        {linkReport && linkReport.length > 0 && (
          <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-900">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" />
                Broken or draft links
              </p>
              <button
                type="button"
                onClick={() => setLinkReport(null)}
                className="text-[11px] font-semibold underline decoration-amber-300 underline-offset-2"
              >
                Dismiss
              </button>
            </div>
            <ul className="mt-1.5 space-y-1">
              {linkReport.map(({ page, issues }) => (
                <li key={page.slug}>
                  <span className="font-semibold">{page.name}</span>
                  {': '}
                  {issues.map((issue, index) => (
                    <span key={`${page.slug}:${issue.href}`}>
                      {index > 0 ? ', ' : ''}
                      <code className="rounded bg-white/70 px-1 py-0.5">{issue.href}</code>
                      <span className="text-amber-700"> ({issue.kind})</span>
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {form && (
            <div className="rounded-xl border border-line bg-shell/50 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-ink">{formTitle}</p>
                <button
                  type="button"
                  onClick={cancelForm}
                  disabled={busy}
                  className="rounded-md p-1 text-muted hover:bg-card disabled:opacity-40"
                  aria-label="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-3 space-y-3">
                <label className="block text-xs font-medium text-ink">
                  Name
                  <input
                    value={name}
                    onChange={(event) => handleNameChange(event.target.value)}
                    className={inputClass}
                    maxLength={100}
                    autoFocus
                  />
                </label>
                {form.kind !== 'rename' && (
                  <label className="block text-xs font-medium text-ink">
                    URL slug
                    <span className="mt-1 flex items-center rounded-lg border border-line bg-shell px-2.5">
                      <span className="text-xs text-muted">/</span>
                      <input
                        value={slug}
                        onChange={(event) => handleSlugChange(event.target.value)}
                        className="w-full bg-transparent py-2 text-xs text-ink outline-none"
                        placeholder="fleet"
                        maxLength={60}
                      />
                    </span>
                    <span className="mt-1 block text-[11px] font-normal text-muted">
                      Lowercase letters, numbers and hyphens. Reserved storefront URLs are not allowed.
                    </span>
                  </label>
                )}
                {form.kind === 'create' && (
                  <div>
                    <p className="text-xs font-medium text-ink">Start from</p>
                    <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                      {PAGE_TEMPLATES.map((option) => {
                        const selected = option.id === template;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setTemplate(option.id)}
                            disabled={busy}
                            aria-pressed={selected}
                            className={`rounded-lg border p-2.5 text-left transition disabled:opacity-50 ${
                              selected
                                ? 'border-brand bg-brand/5 ring-1 ring-brand/30'
                                : 'border-line bg-card hover:border-muted hover:bg-shell'
                            }`}
                          >
                            <span className={`block text-xs font-semibold ${selected ? 'text-brand' : 'text-ink'}`}>
                              {option.label}
                            </span>
                            <span className="mt-0.5 block text-[11px] leading-snug text-muted">
                              {option.description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <span className="mt-1 block text-[11px] text-muted">
                      You can add, remove and reorder sections after the page is created.
                    </span>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancelForm}
                    disabled={busy}
                    className="rounded-lg border border-line px-2.5 py-1.5 text-xs font-semibold text-muted hover:bg-card disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitForm()}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : form.kind === 'rename' ? <Pencil className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    {form.kind === 'rename' ? 'Save name' : form.kind === 'duplicate' ? 'Duplicate page' : 'Create page'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-xs text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading pages…
            </div>
          ) : pages.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted">No pages yet. Create the first one.</p>
          ) : (
            <div className="space-y-2">
              {pages.map((page) => (
                <div
                  key={page.slug}
                  className={`rounded-xl border p-3 ${
                    page.slug === currentSlug ? 'border-brand/40 bg-brand/10' : 'border-line bg-card'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-ink">{page.name}</p>
                      <p className="mt-0.5 text-[11px] text-muted">
                        /{page.slug === 'home' ? '' : page.slug} · {page.sectionCount} section{page.sectionCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          page.isPublished ? 'bg-success/20 text-success' : 'bg-shell text-muted'
                        }`}
                      >
                        {page.isPublished ? 'Published' : 'Draft'}
                      </span>
                      {page.hasUnpublishedChanges && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                          Unpublished changes
                        </span>
                      )}
                      {page.slug === currentSlug && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                          Editing
                        </span>
                      )}
                      {page.slug === currentSlug && dirty && (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                          Unsaved
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => selectPage(page)}
                      disabled={busy}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
                    >
                      {page.slug === currentSlug ? 'Close' : 'Open'}
                    </button>
                    <button
                      type="button"
                      onClick={() => openRename(page)}
                      disabled={busy}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => openDuplicate(page)}
                      disabled={busy}
                      className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-ink hover:bg-shell disabled:opacity-50"
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      onClick={() => void deletePage(page)}
                      disabled={busy || page.slug === 'home'}
                      className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="border-t border-line px-5 py-2 text-[11px] text-muted">
          The Home page supplies the header and footer for every page. Templates start with page content only.
        </footer>
      </div>
    </div>
  );
}
